import type { SubscriptionConfig, SubscriptionTier, TierLimits } from './subscriptionTypes';

// ---- Shared config types ----

export interface MaintenanceConfig {
  enabled: boolean;
  title?: string; // "Under Maintenance"
  message: string; // Dynamic message from backend
  estimatedEndTime?: string; // ISO date / datetime-local string
  allowRetry: boolean; // Show retry button on clients
}

export interface ForceUpdateConfig {
  enabled: boolean;
  minVersion: string; // "1.2.0"
  title?: string;
  message: string;
  storeUrl: {
    ios: string;
    android: string;
  };
}

export const defaultTierLimits: Record<SubscriptionTier, TierLimits> = {
  free: {
    maxProperties: 1,
    maxPhotosPerProperty: 10,
    maxDocumentsPerProperty: 10,
  },
  silver: {
    maxProperties: 3,
    maxPhotosPerProperty: 50,
    maxDocumentsPerProperty: 50,
  },
  gold: {
    maxProperties: 10,
    maxPhotosPerProperty: 200,
    maxDocumentsPerProperty: 200,
  },
  platinum: {
    maxProperties: -1, // Unlimited
    maxPhotosPerProperty: -1,
    maxDocumentsPerProperty: -1,
  },
};

export const defaultSubscriptionConfig: SubscriptionConfig = {
  tiers: defaultTierLimits,
  defaultTier: 'free',
  gracePeriodDays: 7,
};

export interface RemoteConfig {
  maintenance: MaintenanceConfig;
  forceUpdate: ForceUpdateConfig;
  featureFlags: {
    [key: string]: boolean; // e.g., { "newDashboard": true }
  };
  customMessages: {
    [key: string]: string;
  };
  configVersion: number; // For cache invalidation
  lastUpdated: number; // Unix timestamp (seconds)

  documentMaxSize: number;
  defaultMaxOTPAttempts: number;
  imagePickerConfig: {
    quality: 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1;
    maxWidth: number;
    maxHeight: number;
  };
  imageTtl: number;
  defaultRequestTimeout: number;
}

// ---- System settings model (Dynamo row mapped as a key->value map) ----

export const SETTINGS_KEYS = {
  REMOTE_CONFIG: 'REMOTE_CONFIG',
  SUBSCRIPTIONS_CONFIG: 'SUBSCRIPTIONS_CONFIG',
} as const;

export type SettingsKey = (typeof SETTINGS_KEYS)[keyof typeof SETTINGS_KEYS];

// Routing is selected by the web deployment config and the native mobile build.
// This model contains operational remote settings only.
export interface MobileAppConfig {
  REMOTE_CONFIG: RemoteConfig;
  SUBSCRIPTIONS_CONFIG: SubscriptionConfig;
}

export interface FetchSystemSettingsResult {
  settings: MobileAppConfig;
  hydratedFromDefaults: boolean;
}

// ---- API shapes ----

// Strongly-typed update request: key must be a SettingsKey,
// value is tied to that key.
export interface UpdateSettingsRequest {
  updates: Partial<MobileAppConfig>;
}

export interface UpdateSettingsResponse {
  updatedAt: number;
  updatedBy?: string;
  updates: Partial<MobileAppConfig>;
}

export type GetSettingsResponse = MobileAppConfig;

// ---- Redux slice state ----

export interface SettingsState {
  settings: MobileAppConfig | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  hasFetched: boolean;
  hydratedFromDefaults: boolean;
}

const defaultRemoteConfig: RemoteConfig = {
  maintenance: {
    enabled: false,
    title: 'Under Maintenance',
    message: 'We are upgrading our systems. Please check back later.',
    estimatedEndTime: undefined,
    allowRetry: true,
  },
  forceUpdate: {
    enabled: false,
    minVersion: '1.0.0',
    title: 'Update Required',
    message: 'Please update your app to continue.',
    storeUrl: {
      ios: 'https://apps.apple.com/...',
      android: 'https://play.google.com/store/apps/...',
    },
  },
  featureFlags: {},
  customMessages: {},
  configVersion: 1,
  lastUpdated: Math.floor(Date.now() / 1000),

  documentMaxSize: 10485758,
  defaultMaxOTPAttempts: 6,
  imagePickerConfig: {
    quality: 0.6,
    maxWidth: 800,
    maxHeight: 600,
  },
  imageTtl: 3600,
  defaultRequestTimeout: 10000,
};

export const DEFAULT_MOBILE_APP_CONFIG: MobileAppConfig = {
  REMOTE_CONFIG: defaultRemoteConfig,
  SUBSCRIPTIONS_CONFIG: defaultSubscriptionConfig,
};
